import { PostEditorEffectArgs } from '../PostEditorEffects';
import { PostEditorSubmitEditorAction } from '../PostEditorActions';
import { mapStateToPostInput } from '../../helpers/mapStateToPostInput';
import { ApolloError } from '@apollo/client';
import { triggerErrorToast } from '../../../../helpers/triggerErrorToast';
import { showToast } from '../../../../helpers/showToast';
import { validator } from '../../validators/helpers/validator';
import { PostEditorErrors, PostEditorState } from '../../../../models/post-editor';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { PostInputStatus } from '../../../../models/graphql';
import { getCache } from '../../../../apollo/Cache';
import { isArray } from 'lodash';
import { getApolloErrorMessage } from 'helpers/remapApolloErrors';
import { MutableRefObject } from 'react';
import { POST_EDITOR_ERROR_CLASS } from '../../../../constants/styleConstants';

export const validatePostEditorState = (
  state: PostEditorState
): { postGroupErrors: PostEditorErrors[]; isValid: boolean } => {
  let postGroupErrors: PostEditorErrors[] = [];
  state.postGroups.forEach(group => {
    const errors = validator(group, ErrorMessageSeverity.error, group.errors);
    postGroupErrors = [...postGroupErrors, errors];
  });
  // we check ony for error severity, since w emight have draft posts with warrnings, and in that case
  // we don't want to return isValid as false
  const isValid = !postGroupErrors.some(postGroupError => {
    const values = Object.values(postGroupError);
    return values.some(value => {
      // find errors. if errors, return true;
      if (!value) return value;
      if (isArray(value)) {
        // we have an array, we should check if we have severity error in that array
        return value.some(error => error.severity === ErrorMessageSeverity.error);
      } else {
        return value.severity === ErrorMessageSeverity.error;
      }
    });
  }, true);
  return {
    postGroupErrors,
    isValid
  };
};

export const expandAndScrollToFirstPostEditorError = (ref: MutableRefObject<HTMLDivElement | null>): void => {
  // we set timeout because we want this to trigger only after the render process has been done
  setTimeout(() => {
    if (!ref.current) return;
    const errorNode = ref.current.querySelector(`.${POST_EDITOR_ERROR_CLASS}`);
    if (errorNode) {
      errorNode.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  }, 1);
};

export const submitEditorEffect: PostEditorEffectArgs = (state, effect, dispatch) => {
  const {
    type,
    callback,
    workspaceId,
    createPostHookResult,
    updatePostsHookResult,
    profiles,
    context,
    fetchedFamily,
    defaultQueuePosition,
    bulkImportHookResult,
    submitted,
    onPlanLimitException,
    ref
  }: PostEditorSubmitEditorAction['payload'] = effect.payload;
  try {
    if (submitted.current) return;
    const { isValid, postGroupErrors } = validatePostEditorState(state);
    // we dispatch validation and set loading to true. Only if validation result returns with no errors we'll set loader to true
    // in the effect, since we'll start the create / edit / delete mutations
    dispatch({ type: 'setValidation', payload: { isValid, postGroupErrors } });
    if (!isValid) {
      expandAndScrollToFirstPostEditorError(ref);
      return;
    }
    dispatch({ type: 'setLoading', payload: true });
  } catch (e) {
    triggerErrorToast(`Post(s) validation failed`)(e);
    return;
  }
  submitted.current = true;
  if (type === 'single') {
    try {
      const { createPosts, updatePosts, deletePosts } = mapStateToPostInput(
        state,
        profiles,
        defaultQueuePosition,
        fetchedFamily
      );
      let createPromise: Promise<boolean>;
      let updatePromise: Promise<boolean>;
      if (createPosts && createPosts[0].posts.length) {
        createPromise = new Promise(resolve => {
          const [createPost] = createPostHookResult;
          createPost({
            variables: {
              workspaceId,
              input: createPosts[0]
            }
          })
            .then(({ data: result, errors }) => {
              if (errors && errors.length) {
                throw errors[0];
              }
              if (result) {
                // We need to wait the post creation to do the updates when editing to check for the posts limits
                if (updatePosts && updatePosts.length && context === 'update') {
                  updatePromise = new Promise(resolve => {
                    const [updatePostsMutation] = updatePostsHookResult;
                    updatePostsMutation({
                      variables: {
                        input: updatePosts
                      }
                    })
                      .then(({ data: result, errors }) => {
                        if (errors && errors.length) {
                          throw errors[0];
                        }
                        if (result) {
                          showToast({
                            severity: 'success',
                            summary: 'Post(s) updated.',
                            detail: 'Your post(s) have been updated successfully'
                          });
                          resolve(true);
                        }
                      })
                      .catch((e: ApolloError) => {
                        triggerErrorToast('Post(s) not updated.')(e);
                        resolve(false);
                        throw e;
                      });
                  });
                } else {
                  updatePromise = new Promise(resolve => resolve(true));
                }
                // update family if needed
                !updatePosts.length &&
                  showToast({
                    severity: 'success',
                    summary: 'Post created.',
                    detail: 'Your post has been created successfully '
                  });
                resolve(true);
              }
            })
            .catch((e: ApolloError) => {
              if (getApolloErrorMessage(e) === 'PLAN_LIMIT_EXCEEDED' && onPlanLimitException) {
                onPlanLimitException();
                resolve(false);
                throw e;
              }
              triggerErrorToast('Post has not been created')(e);
              resolve(false);
              throw e;
            });
        });
      } else {
        createPromise = new Promise(resolve => {
          resolve(true);
        });
      }

      if (!createPosts && updatePosts && updatePosts.length && context === 'update') {
        updatePromise = new Promise(resolve => {
          const [updatePostsMutation] = updatePostsHookResult;
          updatePostsMutation({
            variables: {
              input: updatePosts
            }
          })
            .then(({ data: result, errors }) => {
              if (errors && errors.length) {
                throw errors[0];
              }
              if (result) {
                showToast({
                  severity: 'success',
                  summary: 'Post(s) updated.',
                  detail: 'Your post(s) have been updated successfully'
                });
                resolve(true);
              }
            })
            .catch((e: ApolloError) => {
              triggerErrorToast('Post(s) not updated.')(e);
              resolve(false);
              throw e;
            });
        });
      } else {
        updatePromise = new Promise(resolve => resolve(true));
      }
      if (deletePosts && deletePosts.length && context === 'update') {
        Promise.allSettled([createPromise, updatePromise]).then(results => {
          const hasOnePromiseFailed = results.some(result => {
            if (result.status === 'fulfilled') {
              return !result.value;
            }
            return true;
          });
          if (hasOnePromiseFailed) {
            dispatch({ type: 'setLoading', payload: false });
          } else {
            const [updatePostsMutation] = updatePostsHookResult;
            updatePostsMutation({
              variables: {
                input: deletePosts.map(id => ({ postId: id, postInput: { status: PostInputStatus.TRASH } }))
              }
            })
              .then(({ data: result, errors }) => {
                if (errors && errors.length) {
                  throw errors[0];
                }
                if (result) {
                  result.bulkUpdatePosts.forEach(deletedPost => {
                    deletedPost.__typename &&
                      getCache().modify({
                        id: getCache().identify({ id: deletedPost.id, __typename: deletedPost.__typename }),
                        fields: {
                          status(_existingStatusRefs, { INVALIDATE }) {
                            return INVALIDATE;
                          }
                        }
                      });
                  });
                  getCache().gc();
                  showToast({
                    severity: 'success',
                    summary: 'Post(s) deleted.',
                    detail: 'The post(s) for the Profiles you unselected have been deleted.'
                  });
                }
                dispatch({ type: 'setLoading', payload: false });
                callback && callback(true);
              })
              .catch((e: ApolloError) => {
                triggerErrorToast('Deleting Post(s) failed.')(e);
                dispatch({ type: 'setLoading', payload: false });
                callback && callback(false);
              });
          }
        });
      } else {
        Promise.allSettled([createPromise, updatePromise]).then(results => {
          const hasOnePromiseFailed = results.some(result => {
            if (result.status === 'fulfilled') {
              return !result.value;
            }
            return true;
          });
          if (hasOnePromiseFailed) {
            dispatch({ type: 'setLoading', payload: false });
          } else {
            dispatch({ type: 'setLoading', payload: false });
            callback && callback(true);
          }
        });
      }
    } catch (e) {
      const word = context === 'update' ? 'Creating' : 'Updating';
      triggerErrorToast(`${word} post(s) failed`)(e);
      callback && callback(false);
      // catch error from mapper
    }
  } else if (type === 'bulk') {
    try {
      const { createPosts } = mapStateToPostInput(state, profiles, defaultQueuePosition, fetchedFamily);
      callback && callback(true);
      if (!createPosts) {
        showToast({
          severity: 'error',
          detail: 'No posts available to import.',
          summary: 'Bulk Import Failed'
        });
        return;
      }
      const [bulkImport] = bulkImportHookResult;
      bulkImport({
        variables: {
          workspaceId,
          input: { postGroupInputs: createPosts }
        }
      })
        .then(result => {
          const { errors } = result;
          if (errors && errors.length) {
            throw errors[0];
          }
          showToast({
            severity: 'success',
            summary: 'Bulk Import Started',
            detail: "Bulk import process has started. We'll inform you once it is done."
          });
        })
        .catch(e => {
          triggerErrorToast(`Bulk Import Failed`)(e);
        });
    } catch (e) {
      callback && callback(false);
      triggerErrorToast(`Bulk Import Failed`)(e);
    }
  }
};
